import { useCallback, useState } from "react"
import { cn } from "@/lib/utils"
import { MetricLabel } from "../MetricLabel/MetricLabel"
import type { KpiTone } from "../types/backtest"

export interface KpiStatItem {
  id: string
  label: string
  value: string
  sub?: string
  tone: KpiTone
  definition?: string
}

export interface KpiStatRowProps {
  items: KpiStatItem[]
  activeId?: string | null
  defaultActiveId?: string | null
  onSelect?: (id: string) => void
  className?: string
}

const TONE_CLASS: Record<KpiTone, string> = {
  success: "text-success",
  danger: "text-danger",
  neutral: "text-foreground",
  warning: "text-amber-500",
}

export function KpiStatRow({
  items,
  activeId: controlled,
  defaultActiveId = null,
  onSelect,
  className,
}: KpiStatRowProps) {
  const [internal, setInternal] = useState<string | null>(defaultActiveId)
  const activeId = controlled ?? internal

  const handleSelect = useCallback(
    (id: string) => {
      setInternal(id)
      onSelect?.(id)
    },
    [onSelect],
  )

  return (
    <div className={cn("flex border-b border-border shrink-0", className)}>
      {items.map((card, i, arr) => {
        const active = activeId === card.id
        return (
          <button
            key={card.id}
            type="button"
            onClick={() => handleSelect(card.id)}
            className={cn(
              "flex-1 min-w-0 flex flex-col items-start gap-0.5 px-2 sm:px-4 py-3 cursor-pointer transition-colors",
              i < arr.length - 1 && "border-r border-border",
              active
                ? "bg-muted [box-shadow:inset_0_-2px_0_0_var(--foreground)]"
                : "hover:bg-muted/70",
            )}
          >
            <MetricLabel
              label={card.label}
              definition={card.definition}
              className={cn(
                "text-caption font-semibold uppercase tracking-normal sm:tracking-wider",
                active ? "text-foreground" : "text-muted-foreground",
              )}
            />
            <span className={cn("text-section sm:text-page-title font-semibold", TONE_CLASS[card.tone])}>
              {card.value}
            </span>
            {card.sub && (
              <span className="text-caption text-muted-foreground">{card.sub}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
